/**
 * What a coach is offered of the Business Builder Accelerator, decided from
 * her account alone: the one rule that the course card, the band in My
 * assets, the offer page and the checkout's refusal all read.
 *
 * Pure, importing only types, so it is safe on either side of the bundle.
 *
 * Aliases: accelerator offer, accelerator state, accelerator card state,
 * accelerator view, who can buy the accelerator
 */

import type { LiveCall } from "~/lib/shared/admin-settings";
import type {
  CoachAccelerator,
  CoachAccount,
} from "~/lib/shared/coach-account";
import { formatStripeAmount, toMajorUnits } from "~/lib/shared/currency";

/** The Accelerator's own page, where its one checkout starts. */
export const ACCELERATOR_OFFER_PATH = "/my-coaching/accelerator";

/**
 * - `member`: she bought it, graduated, or an admin put her in a cohort.
 * - `available`: she can buy it: paid NOVA, on any plan but Pro+.
 * - `paid_plan_needed`: in her trial; it opens with a paid plan.
 * - `preview`: before her trial; information only.
 * - `hidden`: churned and not a member, or on Pro+ without being one.
 */
export type AcceleratorOfferState =
  | "member"
  | "available"
  | "paid_plan_needed"
  | "preview"
  | "hidden";

/**
 * She is in the Accelerator. A cohort counts on its own: an admin sets it when
 * she joins, which may be before any Stripe purchase exists (an earlier cohort,
 * a sale made by hand), and selling her the programme again would charge her
 * twice. Every graduate has a cohort, so `graduate` only makes that explicit.
 */
export function isAcceleratorMember(accelerator: CoachAccelerator): boolean {
  return (
    accelerator.purchased || accelerator.graduate || accelerator.cohort !== null
  );
}

/**
 * The programme still runs for a member: it ends on her graduation date. A
 * member with no date, or one still ahead, is in it. The app stores no cohort
 * calendar, so an admin records the date, possibly in advance.
 *
 * This is not membership. A graduate stays a member after her date, so she is
 * never sold the programme again; only what the programme gives her ends.
 */
export function isAcceleratorProgramRunning(
  graduatedAt: Date | null,
  now: Date,
): boolean {
  return graduatedAt === null || graduatedAt.getTime() > now.getTime();
}

/**
 * The offer for this coach (decision 1: only paid Core and Pro coaches can
 * buy). Membership is read first, whatever the lifecycle: she keeps the
 * Accelerator when NOVA lapses (decision 2).
 *
 * A paid coach whose tier is not resolved yet (see `CoachAccount.tier`) can
 * buy. Pro+ is the only paid tier that cannot, and it is sold to graduates
 * only, so an unknown tier is almost always Core or Pro; refusing her would
 * turn a paying coach away over a missing column.
 */
export function acceleratorOfferState(
  account: Pick<CoachAccount, "lifecycle" | "tier" | "accelerator">,
): AcceleratorOfferState {
  if (isAcceleratorMember(account.accelerator)) return "member";
  switch (account.lifecycle) {
    case "pre_trial":
      return "preview";
    case "trial":
      return "paid_plan_needed";
    case "paid":
      return account.tier === "pro_plus" ? "hidden" : "available";
    case "churned":
      return "hidden";
    default: {
      const exhaustive: never = account.lifecycle;
      throw new Error(`Unknown coach lifecycle: ${String(exhaustive)}`);
    }
  }
}

/** The Accelerator's one-time Stripe price, after the catalogue checks. */
export interface AcceleratorPrice {
  /** The price the offer shows. Checkout buys this one or nothing. */
  readonly priceId: string;
  readonly amountCents: number;
  readonly currency: string;
}

/** "$997", or "$997.50" when the amount has cents. */
export function formatAcceleratorPrice(price: AcceleratorPrice): string {
  const whole = Number.isInteger(
    toMajorUnits(price.amountCents, price.currency),
  );
  return formatStripeAmount(price.amountCents, price.currency, {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/** When the group call runs. Its join link is a separate, members-only field. */
export type AcceleratorCallSchedule = Omit<LiveCall, "joinUrl">;

/**
 * What the Accelerator surfaces show this coach. The group call's schedule is
 * public, since the offer names it; its join link reaches only the coaches who
 * hold the call (`member` while her programme runs, and `pro_plus`), and the
 * server decides that from her coach row.
 *
 * `price` is null when Stripe has no valid Accelerator price: the offer then
 * shows no price and sells nothing.
 */
export type AcceleratorView =
  | { readonly state: "hidden" }
  | {
      readonly state: "preview" | "paid_plan_needed" | "available";
      readonly call: AcceleratorCallSchedule;
      readonly price: AcceleratorPrice | null;
    }
  | {
      /** In the Accelerator, and her programme has not ended. */
      readonly state: "member";
      readonly call: AcceleratorCallSchedule;
      readonly joinUrl: string | null;
    }
  | {
      /**
       * On NOVA Pro+, which includes the weekly call. A graduate whose
       * programme has ended, or a coach who never joined, reads this.
       */
      readonly state: "pro_plus";
      readonly call: AcceleratorCallSchedule;
      readonly joinUrl: string | null;
    };
